import useBellNotifications from './hooks/useBellNotifications';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  AppLayout,
  Flashbar,
  HelpPanel,
  Box,
  Button,
  Modal,
  Header,
  SegmentedControl,
  SpaceBetween,
  Container,
  AnnotationContext
} from '@cloudscape-design/components';
import Link from '@cloudscape-design/components/link';
import Avatar from "@cloudscape-design/chat-components/avatar";
import ChatBubble from "@cloudscape-design/chat-components/chat-bubble";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SideNavigation from './layouts/SideNavigation.js';
import { apiFetch } from './auth/apiClient';
import AppRoutes from './routes/AppRoutes.js'; // Ensure this matches the export in AppRoutes.js
import { helpMessages } from './utils/helpMessages.js';
import CustomSplitPanel from './layouts/CustomSplitPanel.js';
import { useAuth } from './context/AuthContext.js';
import { LocationProvider } from './context/LocationContext';
import { TutorialsContext } from './context/TutorialsContext';
import AdminDashboardHelp from './helpPanelContents/adminDashboardHelp.js';
import FinanceBudgetsHelp from './helpPanelContents/financeBudgetsHelp.js';
import FinanceOverviewHelp from './helpPanelContents/financeOverviewHelp.js';
import RemindersHelp from './helpPanelContents/remindersHelp.js';
import { getReminderBusinessDayDiffDays } from './lib/reminderBusinessDay.js';
		import AdminConsoleIntroHelp from './helpPanelContents/adminConsoleIntroHelp.js';
	import IsetCoordinatorIntroTourHelp from './helpPanelContents/isetCoordinatorIntroTourHelp.js';
	import ApplicationCaseDashboardHelp from './helpPanelContents/applicationCaseDashboardHelp.js';
	import ApplicationAssessmentHelp, { NwacAssessmentHelp } from './helpPanelContents/applicationAssessmentHelp.js';
import CaseWorkspaceHelp from './helpPanelContents/caseWorkspaceHelp.js';
import { MessagingProvider } from './pages/messages/MessagingContext.js';
import FloatingMessageWindow from './pages/messages/FloatingMessageWindow.jsx';
import FloatingAppealWindow from './pages/Caseworking/FloatingAppealWindow.jsx';
import FloatingFeedbackReporter from './features/adminFeedback/FloatingFeedbackReporter.jsx';
import InteractionPanel from './features/minimumService/InteractionPanel.jsx';
import FloatingFeedbackReviewPanel from './features/adminFeedback/FloatingFeedbackReviewPanel.jsx';
import { getReportTypeLabel, getSeverityLabel } from './features/adminFeedback/constants.js';
import {
  buildMaintenanceAnnouncementDisplay,
  isMaintenanceAnnouncementVisible,
} from './utils/maintenanceAnnouncement.js';
import { buildApplicationStatusInfo } from './utils/applicationStatus';
import { buildNotificationTargetPath } from './utils/notificationTarget';
import { buildApplicationWorkspaceTutorials, APPLICATION_WORKSPACE_TUTORIAL_ID } from './tutorials/applicationWorkspaceTutorials';
	import { buildCaseWorkspaceTutorials, CASE_WORKSPACE_TUTORIAL_ID } from './tutorials/caseWorkspaceTutorials';
	import {
	  buildIsetCoordinatorIntroTutorials,
	} from './tutorials/isetCoordinatorIntroTutorials';
	import { buildNwacAssessmentTutorials, NWAC_ASSESSMENT_TUTORIAL_ID } from './tutorials/nwacAssessmentTutorials';
	import { getHomeIntroTutorialIdForRole, isHomeIntroTutorial } from './tutorials/tutorialPlatform';
	import { annotationContextI18nStrings } from './tutorials/tutorialI18n';
	import { useHistory, useLocation } from 'react-router-dom';

const MAX_HISTORY_MESSAGES = 10;
const MAX_STORED_MESSAGES = 24;
const MAX_PROMPT_CHARS = 1000;
const SERVICE_ANNOUNCEMENT_POLL_MS = 15 * 1000;
const TUTORIAL_COMPLETION_STORAGE_KEY = 'iset-tutorials.completed.v1';
const TUTORIAL_APP_LAYOUT_RESET_FLAG = 'iset.tutorial.resetApplicationLayout';
const TUTORIAL_CASE_LAYOUT_RESET_FLAG = 'iset.tutorial.resetCaseWorkspaceLayout';
const NOTIFICATION_TIMEZONE_FALLBACK = 'America/Toronto';
const NOTIFICATION_SORT_STORAGE_KEY = 'iset.notifications.sortMode.v1';
const NOTIFICATION_SORT_MODES = Object.freeze({
  CHRONOLOGICAL: 'chronological',
  URGENCY: 'urgency',
});
const NOTIFICATION_SEVERITY_RANK = Object.freeze({
  critical: 5,
  error: 5,
  warning: 4,
  warn: 4,
  success: 3,
  info: 2,
  'in-progress': 1,
});
const normalizeRoleKey = value => String(value ?? '').trim().toLowerCase();
const APPLICATION_WORKSPACE_PROMPT_ROLE_KEYS = new Set([
  'iset coordinator',
  'nwac administrator',
  'regional manager'
]);
const NWAC_DECISION_PROMPT_ROLE_KEYS = new Set([
  'nwac administrator',
  'system administrator'
]);

const CONTEXT_FACTS = {
  'iset-application-assessment': `
- This workspace combines widgets: Application Overview (case summary), ISET Application Form (editable intake submission), Application Assessment (funding decision workflow), Supporting Documents, Secure Messaging, Case Notes, and Application Events (timeline).
- To correct applicant data, open the ISET Application Form widget and choose **Edit**. Confirm the modal, adjust fields inline, then choose **Save**. Saving creates a new version entry that is accessible via **View versions**; the original submission stays intact.
- Editing is blocked if the case status is already Approved or Denied, except for reporting-only denied applications that remain editable for ILMP corrections. Otherwise, coordinators may update answers on the applicant's behalf when they have source evidence.
- Log the change in Case Notes and, if the applicant must be informed, send a Secure Message from the same board before leaving the page.
- After changes, refresh the Application Overview or Application Events widgets to confirm downstream automations recorded the update.
`
};

const COORDINATOR_PATH_PROMPT_HINTS = [
  'work queue',
  'manage iset applications',
  'manual application intake',
  'application workspace',
  'application assessment',
  'case workspace',
  'participant details',
  'action plans',
  'interventions',
  'supporting documents',
  'secure messaging',
  'case notes',
  'case calendar',
  'application overview'
];

const normaliseKey = (value = '') => value
  .toLowerCase()
  .replace(/&amp;/g, 'and')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const isCoordinatorPathWorkflowContext = ({ focusTitle, aiContext }) => {
  const combined = `${focusTitle || ''} ${aiContext || ''}`.toLowerCase();
  return COORDINATOR_PATH_PROMPT_HINTS.some(hint => combined.includes(hint));
};

const buildSystemPrompt = ({ focusTitle, aiContext }) => {
  const safeContext = (aiContext || '').trim();
  const sections = [
    'You are "Admin Copilot", an embedded assistant inside the ISET Admin Dashboard powered by AWS Cloudscape.',
    `Focus area for this session: ${focusTitle || 'General admin dashboard guidance'}.`
  ];

  if (safeContext) {
    sections.push(`Key contextual hints: ${safeContext}`);
  } else {
    sections.push('No additional AI context was provided. Ask for specifics when the request is ambiguous.');
  }

  const hintCandidates = [focusTitle, safeContext]
    .filter(Boolean)
    .map(normaliseKey);
  const matchedHints = hintCandidates
    .map(key => CONTEXT_FACTS[key])
    .find(Boolean);
  if (matchedHints) {
    sections.push('', 'Workflow specifics:', matchedHints.trim());
  }

  if (isCoordinatorPathWorkflowContext({ focusTitle, aiContext: safeContext })) {
    sections.push(
      '',
      'PATH staff-support lens:',
      '- Answer like a PATH job aid for staff, not like a product tour or technical architecture note.',
      '- When the user asks what to do next, structure the answer around: immediate next action, where in PATH to do it, what to document, and any important timing or policy reminder.',
      '- Prefer specific PATH areas and actions such as Work Queue, Manage ISET Applications, Application Workspace, Case Workspace, Supporting Documents, Secure Messaging, and Notes rather than vague phrases like "use the appropriate channel".',
      '- If the question is yes/no or can/cannot, lead with the direct answer first and then explain the condition.',
      '- Reinforce training-aligned habits when relevant: prompt acknowledgement, documenting interactions, tracking missing-document follow-up attempts, supporting living-allowance decisions with financial evidence, and completing required post-intervention follow-up, including 12-week follow-up where applicable, before closure.',
      '- When the question is about historical client-file records, explain the Case Header quick actions `Add existing action plan`, `Add existing intervention`, and `Upload existing documents` as tools for adding records that already existed before PATH.',
      '- Keep future approved interventions Approved until service starts. Suspended means a temporary pause after service has started and remains ILMP-reportable. Future-dated activation or suspension shows a dismissible message; it does not remove Activate or Status controls or discard unsaved edits. Incorrect dates follow existing edit/revision permissions.',
      '- For backloaded interventions, mention the main guardrails when relevant: archived plans cannot receive them, closed plans only accept completed/cancelled interventions, in-progress or suspended interventions require an active plan, and backloaded actual amounts or payment lines are historical finance history only.',
      '- For historical supporting documents, keep the answer practical: use Upload existing documents and attach the file to the correct client, case, or action plan.',
      '- Do not invent controls, workflow steps, or permissions that are not in the current help context.'
    );
  }

  sections.push(
    '',
    'Guidelines:',
    '1. Stay on topic—only address the admin dashboard, its workflows, or related operations.',
    '2. Ask clarifying questions only when the goal or data is genuinely unclear; if the current help context is enough to answer a standard workflow question, answer directly.',
    '3. Write as a staff job aid: explain the relevant task and next action using the visible UI labels. Keep implementation detail out unless needed to answer a technical question.',
    '4. Format lists, tables, and code samples using GitHub-flavored Markdown.',
    '5. Lead with the answer or next action. Use only the workflow context relevant to the question; do not recite the supplied reference material. Keep responses focused and under roughly eight sentences unless additional depth is requested.',
    '6. Never fabricate data, credentials, controls, permissions or system behavior. Do not substitute development history or design-discussion rationale for help using the page. Distinguish recorded facts from estimates; do not claim to have checked live figures unless they were supplied. If uncertain, say so.',
    '7. End cleanly once the answer is complete. Do not append generic offers like "I can help draft that" or an extra follow-up question unless the user explicitly asked for wording help or the next action truly depends on it.',
    `Current date: ${new Date().toISOString().split('T')[0]}.`
  );

  return sections.join('\n');
};

const getAiAssistantErrorMessage = data => {
  const nestedMessage = data?.details?.message || data?.details?.error?.message || '';
  if (typeof nestedMessage === 'string' && /No endpoints found for /i.test(nestedMessage)) {
    return 'The configured AI model is no longer available. Go to Configuration Settings > AI / LLM Configuration, choose a current model, and save.';
  }
  return data?.message || nestedMessage || 'AI assistant is disabled or unavailable.';
};

const parseNotificationMetadata = (value) => {
  try {
    if (!value) return {};
    if (typeof value === 'string') return JSON.parse(value) || {};
    if (typeof value === 'object') return value || {};
  } catch (_) {}
  return {};
};

const getNotificationViewerTimeZone = () => {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') {
    return NOTIFICATION_TIMEZONE_FALLBACK;
  }
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions?.();
    const candidate = typeof resolved?.timeZone === 'string' ? resolved.timeZone.trim() : '';
    return candidate || NOTIFICATION_TIMEZONE_FALLBACK;
  } catch (_) {
    return NOTIFICATION_TIMEZONE_FALLBACK;
  }
};

const formatNotificationTimestamp = (value, timeZone = NOTIFICATION_TIMEZONE_FALLBACK) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      weekday: 'short',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
    const parts = formatter.formatToParts(date);
    const lookup = {};
    for (const part of parts) {
      if (part.type === 'literal') continue;
      lookup[part.type] = lookup[part.type] ? `${lookup[part.type]}${part.value}` : part.value;
    }

    const weekday = lookup.weekday || '';
    const month = lookup.month || '';
    const day = lookup.day || '';
    const year = lookup.year || '';
    const hour = lookup.hour || '';
    const minute = lookup.minute || '';
    const timeZoneName = lookup.timeZoneName || '';
    const dayPeriod = String(lookup.dayPeriod || '').replace(/\./g, '').toLowerCase();
    const core = `${weekday} ${month} ${day}, ${year} ${hour}:${minute}${dayPeriod}`.replace(/\s+/g, ' ').trim();

    return timeZoneName ? `${core} ${timeZoneName}` : core;
  } catch (_) {
    try {
      return date.toLocaleString('en-CA', {
        timeZone,
        weekday: 'short',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short',
      });
    } catch (_) {
      return date.toLocaleString('en-CA');
    }
  }
};

const normalizeNotificationSortMode = (value) =>
  value === NOTIFICATION_SORT_MODES.URGENCY
    ? NOTIFICATION_SORT_MODES.URGENCY
    : NOTIFICATION_SORT_MODES.CHRONOLOGICAL;

const getStoredNotificationSortMode = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return NOTIFICATION_SORT_MODES.CHRONOLOGICAL;
  }
  try {
    return normalizeNotificationSortMode(window.localStorage.getItem(NOTIFICATION_SORT_STORAGE_KEY));
  } catch (_) {
    return NOTIFICATION_SORT_MODES.CHRONOLOGICAL;
  }
};

const persistNotificationSortMode = (value) => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(NOTIFICATION_SORT_STORAGE_KEY, normalizeNotificationSortMode(value));
  } catch (_) {}
};

const getNotificationSortTimestampMs = (notification) => {
  const rawValue = notification?.delivered_at || notification?.created_at || notification?.updated_at || null;
  if (!rawValue) return 0;
  const timestamp = rawValue instanceof Date ? rawValue.getTime() : Date.parse(rawValue);
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const getNotificationSortId = (notification) => {
  const numeric = Number(notification?.id);
  return Number.isFinite(numeric) ? numeric : 0;
};

const getNotificationSeverityRank = (notification) => {
  const key = String(notification?.severity || 'info').trim().toLowerCase();
  return NOTIFICATION_SEVERITY_RANK[key] || 1;
};

const compareNotificationsChronologically = (left, right) => {
  const timestampDelta = getNotificationSortTimestampMs(right) - getNotificationSortTimestampMs(left);
  if (timestampDelta !== 0) return timestampDelta;
  return getNotificationSortId(right) - getNotificationSortId(left);
};

const compareNotificationsByUrgency = (left, right) => {
  const severityDelta = getNotificationSeverityRank(right) - getNotificationSeverityRank(left);
  if (severityDelta !== 0) return severityDelta;
  return compareNotificationsChronologically(left, right);
};

const sortNotificationsForDisplay = (items, sortMode) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const sorter = normalizeNotificationSortMode(sortMode) === NOTIFICATION_SORT_MODES.URGENCY
    ? compareNotificationsByUrgency
    : compareNotificationsChronologically;
  return [...items].sort(sorter);
};

const loadTutorialCompletionMapFromLocalStorage = () => {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = window.localStorage?.getItem(TUTORIAL_COMPLETION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.reduce((acc, entry) => {
        if (typeof entry === 'string' && entry.trim()) {
          acc[entry] = true;
        }
        return acc;
      }, {});
    }
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
  } catch (_) {
    return {};
  }
  return {};
};

const clearTutorialCompletionMapInLocalStorage = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage?.removeItem(TUTORIAL_COMPLETION_STORAGE_KEY);
  } catch (_) {
    // Ignore persistence failures (private mode, quota, etc.)
  }
};

const getTutorialId = (tutorial) => {
  if (!tutorial) return '';
  if (typeof tutorial.tutorialId === 'string' && tutorial.tutorialId.trim()) {
    return tutorial.tutorialId.trim();
  }
  if (typeof tutorial.title === 'string' && tutorial.title.trim()) {
    return tutorial.title.trim();
  }
  return '';
};

const cloneTutorialForRun = (tutorial) => {
  if (!tutorial || typeof tutorial !== 'object') return null;
  return {
    ...tutorial,
    completed: false,
    tasks: (tutorial.tasks || []).map((task) => ({
      ...task,
      steps: (task.steps || []).map((step) => ({ ...step }))
    }))
  };
};

const createChatMessage = (type, content) => {
  const now = new Date();
  const text = typeof content === 'string' ? content : String(content ?? '');
  return {
    id: `${type}-${now.getTime()}-${Math.random().toString(16).slice(2, 10)}`,
    type,
    text,
    timestamp: now.toISOString(),
    displayTime: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  };
};

const mapChatToOpenAi = (message) => {
  const safeText = typeof message.text === 'string' ? message.text : String(message.text ?? '');
  return {
    role: message.type === 'incoming' ? 'assistant' : 'user',
    content: safeText
  };
};

const sanitizeAssistantText = (rawText) => {
  if (typeof rawText !== 'string') {
    return '';
  }

  return rawText
    .replace(/<\/?s>/gi, '')
    .replace(/\[\/?(?:out|in|sys|inst)\]/gi, '')
    .replace(/^\s*::(?:out|in):/i, '')
    .trim();
};

const FloatingChat = React.memo(function FloatingChat({
  visible,
  aiContext,
  pathname,
  onClose,
  role,
  title
}) {
  const [chatMessages, setChatMessages] = useState([]);
  const [promptValue, setPromptValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const chatContainerRef = useRef(null);
  const chatInputRef = useRef(null);

  const markdownPlugins = useMemo(() => [remarkGfm], []);
  const markdownComponents = useMemo(() => ({
    a: ({ children, ...props }) => (
      <a
        {...props}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'var(--color-text-link-default, #2563eb)' }}
      >
        {children}
      </a>
    ),
    code: ({ inline, children, ...props }) => {
      const codeContent = String(children).replace(/\n$/, '');
      if (inline) {
        return (
          <code
            {...props}
            style={{
              backgroundColor: 'rgba(15, 23, 42, 0.06)',
              padding: '0.15rem 0.35rem',
              borderRadius: '4px',
              fontSize: '0.95em',
              fontFamily: 'var(--font-family-monospace, "Source Code Pro", monospace)'
            }}
          >
            {codeContent}
          </code>
        );
      }
      return (
        <pre
          {...props}
          style={{
            backgroundColor: 'rgba(15, 23, 42, 0.08)',
            borderRadius: '10px',
            padding: '0.75rem',
            overflowX: 'auto',
            margin: '0.5rem 0',
            fontFamily: 'var(--font-family-monospace, "Source Code Pro", monospace)'
          }}
        >
          <code>{codeContent}</code>
        </pre>
      );
    },
    ul: ({ children }) => (
      <ul style={{ paddingLeft: '1.2rem', margin: '0.35rem 0 0.6rem' }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ paddingLeft: '1.2rem', margin: '0.35rem 0 0.6rem' }}>{children}</ol>
    ),
    li: ({ children }) => (
      <li style={{ marginBottom: '0.25rem' }}>{children}</li>
    ),
    strong: ({ children }) => (
      <strong style={{ fontWeight: 600 }}>{children}</strong>
    ),
    blockquote: ({ children }) => (
      <blockquote
        style={{
          borderLeft: '4px solid rgba(15, 23, 42, 0.15)',
          margin: '0.5rem 0',
          padding: '0.25rem 0 0.25rem 0.75rem',
          color: 'var(--color-text-body-secondary, #475569)'
        }}
      >
        {children}
      </blockquote>
    )
  }), []);

  const appendMessage = useCallback((message) => {
    setChatMessages(prev => {
      const next = [...prev, message];
      return next.length > MAX_STORED_MESSAGES ? next.slice(next.length - MAX_STORED_MESSAGES) : next;
    });
  }, []);

  const handleSendMessage = useCallback(async () => {
    const trimmed = promptValue.trim();
    if (!trimmed || loading) {
      return;
    }

    const recentHistory = chatMessages.slice(-MAX_HISTORY_MESSAGES);
    const userMessage = createChatMessage('outgoing', trimmed);

    appendMessage(userMessage);
    setPromptValue('');
    setLoading(true);

    try {
      const payloadMessages = [
        { role: 'system', content: buildSystemPrompt({ focusTitle: title, aiContext }) },
        ...recentHistory.map(mapChatToOpenAi),
        { role: 'user', content: trimmed }
      ];

      const response = await apiFetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatContext: {
            surface: 'help-panel',
            pathname,
            helpTitle: title,
            aiContext,
            role
          },
          temperature: 0.4,
          messages: payloadMessages
        })
      });

      const data = await response.json();
      const messageText = response.ok
        ? data.choices?.[0]?.message?.content || 'Sorry, I didn’t understand that.'
        : getAiAssistantErrorMessage(data);

      appendMessage(createChatMessage('incoming', sanitizeAssistantText(messageText)));
    } catch (error) {
      console.error('AI error:', error);
      appendMessage(createChatMessage('incoming', 'Something went wrong. Please try again later.'));
    } finally {
      setLoading(false);
    }
  }, [promptValue, loading, chatMessages, aiContext, pathname, role, title, appendMessage]);

  useEffect(() => {
    if (visible && chatInputRef.current) {
      const timeout = window.requestAnimationFrame(() => {
        chatInputRef.current?.focus();
      });
      return () => window.cancelAnimationFrame(timeout);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    const node = chatContainerRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [chatMessages, visible]);

  const sendDisabled = loading || !promptValue.trim();

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="AI assistant chat"
      style={{
        position: 'fixed',
        bottom: '2rem',
        right: '2rem',
        width: 'min(420px, calc(100vw - 3rem))',
        maxWidth: '100%',
        maxHeight: 'calc(100vh - 4rem)',
        zIndex: 2000,
        pointerEvents: visible ? 'auto' : 'none',
        display: visible ? 'block' : 'none'
      }}
      aria-hidden={!visible}
    >
      <Container
        header={
          <Header
            variant="h2"
            actions={
              <Button iconName="close" variant="icon" ariaLabel="Close AI chat" onClick={onClose} />
            }
          >
            Ask the AI
          </Header>
        }
        footer={
          <form
            onSubmit={(event) => {
              event.preventDefault();
              handleSendMessage();
            }}
            style={{ width: '100%' }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: '0.5rem',
                backgroundColor: 'var(--color-background-input-default, #ffffff)',
                border: '1px solid var(--color-border-input-default, #aab7b8)',
                borderRadius: '999px',
                padding: '0.35rem 0.75rem',
                transition: 'box-shadow 120ms ease, border-color 120ms ease',
                boxShadow: inputFocused ? '0 0 0 2px rgba(9, 114, 211, 0.35)' : 'none'
              }}
            >
              <textarea
                ref={chatInputRef}
                value={promptValue}
                maxLength={MAX_PROMPT_CHARS}
                onChange={(event) => setPromptValue(event.target.value)}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleSendMessage();
                  }
                }}
                placeholder="Ask me anything about this page..."
                rows={1}
                style={{
                  flexGrow: 1,
                  resize: 'vertical',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  color: 'var(--color-text-body-default, #0f172a)',
                  fontSize: '0.95rem',
                  lineHeight: 1.5,
                  padding: '0.35rem 0',
                  minHeight: '2.5rem',
                  maxHeight: '8rem',
                  width: '100%'
                }}
              />
              <Button
                type="submit"
                variant="icon"
                iconName="send"
                loading={loading}
                disabled={sendDisabled}
                ariaLabel="Send message"
                style={{
                  backgroundColor: sendDisabled ? 'var(--color-background-button-icon-disabled, #f2f3f3)' : 'var(--color-background-button-primary-default, #0972d3)',
                  color: sendDisabled ? 'var(--color-text-button-icon-disabled, #aab7b8)' : '#ffffff',
                  borderRadius: '999px',
                  width: '2.75rem',
                  height: '2.75rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </div>
            <div
              style={{
                marginTop: '0.25rem',
                fontSize: '0.75rem',
                color: 'var(--color-text-body-secondary, #6b7280)',
                textAlign: 'right'
              }}
            >
              Max {MAX_PROMPT_CHARS.toLocaleString()} characters
            </div>
          </form>
        }
        style={{
          boxShadow: '0 16px 40px rgba(15, 23, 42, 0.35)',
          borderRadius: '16px',
          overflow: 'hidden'
        }}
      >
        <Box margin={{ bottom: 's' }}>
          <SpaceBetween size="s">
            <div
              ref={chatContainerRef}
              style={{
                height: 'min(420px, calc(100vh - 12rem))',
                overflowY: 'auto',
                paddingRight: '0.5rem'
              }}
            >
              {chatMessages.length === 0 && !loading && (
                <Box variant="p" style={{ color: 'var(--color-text-body-secondary, #4b5563)' }}>
                  Share what you’re working on or paste an error message—responses stay scoped to the current help topic.
                </Box>
              )}
              {chatMessages.map((message, index) => {
                const timeLabel = message.displayTime || message.timestamp || '';
                return (
                  <ChatBubble
                    key={message.id || `${message.timestamp}-${index}`}
                    ariaLabel={`${message.type === 'outgoing' ? 'You' : 'AI'} at ${timeLabel}`}
                    type={message.type}
                    avatar={
                      message.type === 'outgoing' ? (
                        <Avatar
                          ariaLabel="You"
                          tooltipText="You"
                          iconName="user-profile"
                          color="default"
                        />
                      ) : (
                        <Avatar
                          color="gen-ai"
                          iconName="gen-ai"
                          ariaLabel="Generative AI assistant"
                          tooltipText="Generative AI assistant"
                        />
                      )
                    }
                  >
                    <div
                      style={{
                        display: 'block',
                        color: 'var(--color-text-body-default, #0f172a)',
                        fontSize: '0.95rem',
                        lineHeight: 1.55
                      }}
                    >
                      <ReactMarkdown
                        remarkPlugins={markdownPlugins}
                        components={markdownComponents}
                      >
                        {message.text}
                      </ReactMarkdown>
                    </div>
                  </ChatBubble>
                );
              })}
              {loading && (
                <Box variant="p" style={{ color: 'var(--color-text-body-secondary, #4b5563)' }}>
                  Generating a response...
                </Box>
              )}
            </div>
            <Box variant="p" style={{ fontSize: '0.85rem', color: 'var(--color-text-body-secondary, #4b5563)' }}>
              Tip: Shift + Enter adds a newline. Markdown works for lists and code. Responses stay focused on this help topic.
            </Box>
          </SpaceBetween>
        </Box>
      </Container>
    </div>
  );
});

const AppContent = () => {
  const { role, currentUser, isAuthenticated } = useAuth();
  const [currentHelpContent, setCurrentHelpContent] = useState(helpMessages.overview);
  const [isHelpPanelOpen, setIsHelpPanelOpen] = useState(false);
  const [helpPanelTitle, setHelpPanelTitle] = useState("Help Panel");
  const [isNavigationOpen, setIsNavigationOpen] = useState(true);
  const [breadcrumbs, setBreadcrumbs] = useState([{ text: 'Home', href: '/' }, { text: 'Admin Console', href: '#' }]);
  const [splitPanelOpen, setSplitPanelOpen] = useState(false); // State for SplitPanel, initially closed
  const [splitPanelSize, setSplitPanelSize] = useState(360); // State for SplitPanel size
  const [splitPanelPreferences, setSplitPanelPreferences] = useState({ position: 'side' }); // State for SplitPanel preferences
	const [availableItems, setAvailableItems] = useState([]); // State for available items (palette)
	const location = useLocation();
	const history = useHistory();
	const [tutorialStatusMap, setTutorialStatusMap] = useState({});
  const [tutorialProgressLoading, setTutorialProgressLoading] = useState(true);
  const migrateTutorialProgressRef = useRef(false);
  const [currentTutorial, setCurrentTutorial] = useState(null);
  const currentTutorialRef = useRef(null);
	  const [introPromptVisible, setIntroPromptVisible] = useState(false);
	  const introPromptShownRef = useRef(false);
	  const [pageTutorialPrompt, setPageTutorialPrompt] = useState({ visible: false, tutorialId: null });
	  const pageTutorialPromptShownRef = useRef(new Set());
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [feedbackOpenRequestId, setFeedbackOpenRequestId] = useState(0);
  const [feedbackRequestedType, setFeedbackRequestedType] = useState('bug');
  const [feedbackRequestedContextSnapshot, setFeedbackRequestedContextSnapshot] = useState(null);
  const [feedbackReviewVisible, setFeedbackReviewVisible] = useState(false);
  const [feedbackReviewOpenRequestId, setFeedbackReviewOpenRequestId] = useState(0);
  const [feedbackReviewReportId, setFeedbackReviewReportId] = useState(null);
  const [feedbackSubmissionConfirmation, setFeedbackSubmissionConfirmation] = useState(null);

  const effectiveRole = role || '';
  const normalizedEffectiveRole = useMemo(
    () => normalizeRoleKey(effectiveRole),
    [effectiveRole]
  );

  const homeIntroTutorialId = useMemo(
    () => getHomeIntroTutorialIdForRole(effectiveRole),
    [effectiveRole]
  );

  const completedTutorials = useMemo(() => {
    const next = {};
    Object.entries(tutorialStatusMap || {}).forEach(([tutorialId, status]) => {
      if (status === 'completed') {
        next[tutorialId] = true;
      }
    });
    return next;
  }, [tutorialStatusMap]);

  const tutorials = useMemo(
    () => [
      ...buildIsetCoordinatorIntroTutorials({ completedMap: completedTutorials }),
      ...buildApplicationWorkspaceTutorials({ completedMap: completedTutorials }),
      ...buildCaseWorkspaceTutorials({ completedMap: completedTutorials }),
      ...buildNwacAssessmentTutorials({ completedMap: completedTutorials })
    ],
    [completedTutorials]
  );

  const loadTutorialProgress = useCallback(async () => {
    setTutorialProgressLoading(true);
    try {
      const response = await apiFetch('/api/me/tutorial-progress');
      let data = null;
      try { data = await response.json(); } catch { data = null; }
      if (!response.ok) {
        const message = data?.error || 'Failed to load tutorial progress';
        throw new Error(message);
      }
      const items = Array.isArray(data?.items) ? data.items : [];
      const map = items.reduce((acc, item) => {
        const tutorialId = typeof item?.tutorialId === 'string' ? item.tutorialId.trim() : '';
        const status = typeof item?.status === 'string' ? item.status.trim().toLowerCase() : '';
        if (tutorialId && (status === 'completed' || status === 'dismissed')) {
          acc[tutorialId] = status;
        }
        return acc;
      }, {});
      setTutorialStatusMap(map);
      return map;
    } catch (error) {
      console.error('[tutorials] load progress failed', error);
      setTutorialStatusMap({});
      return null;
    } finally {
      setTutorialProgressLoading(false);
    }
  }, []);

  const persistTutorialStatus = useCallback(async (tutorialId, status) => {
    const id = typeof tutorialId === 'string' ? tutorialId.trim() : '';
    const normalisedStatus = typeof status === 'string' ? status.trim().toLowerCase() : '';
    if (!id) return false;
    if (!['completed', 'dismissed'].includes(normalisedStatus)) return false;
    try {
      const response = await apiFetch('/api/me/tutorial-progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tutorialId: id, status: normalisedStatus })
      });
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Failed to persist tutorial status (${response.status})`);
      }
      setTutorialStatusMap(prev => ({ ...(prev || {}), [id]: normalisedStatus }));
      return true;
    } catch (error) {
      console.error('[tutorials] persist failed', error);
      return false;
    }
  }, []);

  useEffect(() => {
    loadTutorialProgress();
  }, [loadTutorialProgress]);

  useEffect(() => {
    const handleAuthChange = () => {
      loadTutorialProgress();
    };
    window.addEventListener('auth:session-changed', handleAuthChange);
    window.addEventListener('storage', handleAuthChange);
    return () => {
      window.removeEventListener('auth:session-changed', handleAuthChange);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, [loadTutorialProgress]);

  useEffect(() => {
    const handler = () => {
      introPromptShownRef.current = false;
      pageTutorialPromptShownRef.current = new Set();
      loadTutorialProgress();
    };
    window.addEventListener('tutorials:refresh', handler);
    return () => window.removeEventListener('tutorials:refresh', handler);
  }, [loadTutorialProgress]);

  useEffect(() => {
    if (!homeIntroTutorialId) return;
    const status = tutorialStatusMap[homeIntroTutorialId];
    if (status !== 'completed' && status !== 'dismissed') {
      introPromptShownRef.current = false;
    }
  }, [homeIntroTutorialId, tutorialStatusMap]);

  useEffect(() => {
    if (tutorialProgressLoading) return;
    if (migrateTutorialProgressRef.current) return;
    migrateTutorialProgressRef.current = true;

    const localCompletedMap = loadTutorialCompletionMapFromLocalStorage();
    const localIds = Object.keys(localCompletedMap || {}).filter((key) => localCompletedMap[key]);
    if (!localIds.length) {
      clearTutorialCompletionMapInLocalStorage();
      return;
    }

    const missing = localIds.filter((id) => tutorialStatusMap[id] !== 'completed');
    if (!missing.length) {
      clearTutorialCompletionMapInLocalStorage();
      return;
    }

    (async () => {
      try {
        const response = await apiFetch('/api/me/tutorial-progress/bulk-complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tutorialIds: missing })
        });
        if (!response.ok) {
          const message = await response.text();
          throw new Error(message || `Failed to migrate tutorial progress (${response.status})`);
        }
        clearTutorialCompletionMapInLocalStorage();
        await loadTutorialProgress();
      } catch (error) {
        console.error('[tutorials] migration failed', error);
      }
    })();
  }, [tutorialProgressLoading, tutorialStatusMap, loadTutorialProgress]);

	  useEffect(() => {
	    if (introPromptShownRef.current) return;
	    if (tutorialProgressLoading) return;
	    if ((location?.pathname || '/') !== '/') return;
	    if (!homeIntroTutorialId) return;
	    const status = tutorialStatusMap[homeIntroTutorialId];
	    if (status === 'completed' || status === 'dismissed') return;
	    introPromptShownRef.current = true;
	    setIntroPromptVisible(true);
	  }, [homeIntroTutorialId, location?.pathname, tutorialProgressLoading, tutorialStatusMap]);

	  useEffect(() => {
	    if (introPromptVisible) return;
	    if (pageTutorialPrompt.visible) return;
	    if (tutorialProgressLoading) return;
      if (currentTutorial) return;

	    const path = location?.pathname || '/';
	    const match = path.match(/^\/application-case\/(\d+)/);
	    if (!match) return;
	    const caseId = match[1];
	    const maybePromptApplicationWorkspaceTutorial = () => {
	      if (!APPLICATION_WORKSPACE_PROMPT_ROLE_KEYS.has(normalizedEffectiveRole)) return;
	      const status = tutorialStatusMap[APPLICATION_WORKSPACE_TUTORIAL_ID];
	      if (status === 'completed' || status === 'dismissed') return;
	      const key = `${APPLICATION_WORKSPACE_TUTORIAL_ID}:${caseId}`;
	      if (pageTutorialPromptShownRef.current.has(key)) return;
	      pageTutorialPromptShownRef.current.add(key);
	      setPageTutorialPrompt({ visible: true, tutorialId: APPLICATION_WORKSPACE_TUTORIAL_ID });
	    };

	    // NWAC decision tutorial: prompt NWAC reviewers only when the case is in pending approval.
	    if (NWAC_DECISION_PROMPT_ROLE_KEYS.has(normalizedEffectiveRole)) {
	      const nwacStatus = tutorialStatusMap[NWAC_ASSESSMENT_TUTORIAL_ID];
	      const nwacKey = `${NWAC_ASSESSMENT_TUTORIAL_ID}:${caseId}`;
	      if (nwacStatus === 'completed' || nwacStatus === 'dismissed' || pageTutorialPromptShownRef.current.has(nwacKey)) {
	        maybePromptApplicationWorkspaceTutorial();
	        return;
	      }

	      let cancelled = false;
	      (async () => {
	        try {
	          const resp = await apiFetch(`/api/cases/${caseId}`);
	          const json = await resp.json().catch(() => null);
	          if (cancelled || !resp.ok) return;
	          const caseStatus = json?.status || json?.case?.status || null;
	          const statusInfo = buildApplicationStatusInfo({
	            applicationStatus: json?.application_status || json?.applicationStatus || null,
	            applicationLifecycleStatus:
	              json?.application_lifecycle_status || json?.applicationLifecycleStatus || null,
	            caseStatus,
	            caseId,
	            decisionOutcome: json?.decision_outcome || json?.decisionOutcome || null,
	            awaitingReason:
	              json?.application_awaiting_reason || json?.applicationAwaitingReason || null,
	            closureReason:
	              json?.application_closure_reason || json?.applicationClosureReason || null,
	            reviewStatus: json?.review_status || json?.reviewStatus || null,
	          });
	          if (statusInfo.rawStatus === 'pending_approval') {
	            pageTutorialPromptShownRef.current.add(nwacKey);
	            setPageTutorialPrompt({ visible: true, tutorialId: NWAC_ASSESSMENT_TUTORIAL_ID });
	            return;
	          }
	          maybePromptApplicationWorkspaceTutorial();
	        } catch (_) {
	          // If we cannot determine NWAC state, still allow the workspace overview tutorial prompt.
	          maybePromptApplicationWorkspaceTutorial();
	        }
	      })();

	      return () => { cancelled = true; };
	    }

	    maybePromptApplicationWorkspaceTutorial();
	  }, [
	    introPromptVisible,
	    pageTutorialPrompt.visible,
      currentTutorial,
	    normalizedEffectiveRole,
	    location?.pathname,
	    tutorialProgressLoading,
	    tutorialStatusMap,
	  ]);

  useEffect(() => {
    if (introPromptVisible) return;
    if (pageTutorialPrompt.visible) return;
    if (tutorialProgressLoading) return;
    if (currentTutorial) return;

    const path = location?.pathname || '/';
    if (/^\/cases\/\d+\/appeals(?:\/|$)/.test(path)) return;
    const match = path.match(/^\/cases\/(\d+)/);
    if (!match) return;
    const caseId = match[1];

    const status = tutorialStatusMap[CASE_WORKSPACE_TUTORIAL_ID];
    if (status === 'completed' || status === 'dismissed') return;
    const key = `${CASE_WORKSPACE_TUTORIAL_ID}:${caseId}`;
    if (pageTutorialPromptShownRef.current.has(key)) return;
    pageTutorialPromptShownRef.current.add(key);
    setPageTutorialPrompt({ visible: true, tutorialId: CASE_WORKSPACE_TUTORIAL_ID });
  }, [
    introPromptVisible,
    pageTutorialPrompt.visible,
    currentTutorial,
    location?.pathname,
    tutorialProgressLoading,
    tutorialStatusMap
  ]);

  // Notifications and service announcements
  const [dismissingNotifications, setDismissingNotifications] = useState(false);
  const [notificationDismissError, setNotificationDismissError] = useState('');
  const dismissAllInFlight = useRef(false);
  const [notificationSortMode, setNotificationSortMode] = useState(getStoredNotificationSortMode);
  const notificationIdentityKey = isAuthenticated && !currentUser.loading && currentUser.userId
    ? `${currentUser.staffProfileId || currentUser.userId || currentUser.email || ''}:${role || ''}` : '';
  const { notifications, loading: notificationsLoading, refresh: fetchNotifications, remove: removeNotification }
    = useBellNotifications(notificationIdentityKey, normalizeNotificationSortMode(notificationSortMode));
  const [serviceAnnouncement, setServiceAnnouncement] = useState(null);
  const [announcementNow, setAnnouncementNow] = useState(() => new Date());
  const flashbarRef = useRef(null);
  const notificationViewerTimeZone = useMemo(() => getNotificationViewerTimeZone(), []);

  const notificationScope = useMemo(() => {
    const path = location?.pathname || '/';
    const caseMatch = path.match(/^\/cases\/(\d+)(?:\/|$)/);
    if (caseMatch) {
      return { type: 'case', id: caseMatch[1] };
    }
    const appMatch = path.match(/^\/application-case\/([^/]+)(?:\/|$)/);
    if (appMatch) {
      return { type: 'application', id: appMatch[1] };
    }
    return { type: 'all', id: null };
  }, [location?.pathname]);

  const scopedNotifications = useMemo(() => {
    if (!Array.isArray(notifications) || notifications.length === 0) return [];
    if (!notificationScope || notificationScope.type === 'all' || !notificationScope.id) {
      return sortNotificationsForDisplay(notifications, notificationSortMode);
    }

    const id = String(notificationScope.id);
    const isNumericId = /^\d+$/.test(id);

    const filteredNotifications = notifications.filter((n) => {
      if (!n) return false;
      const metadata = parseNotificationMetadata(n.metadata);
      const metaCaseId = metadata.caseId != null ? String(metadata.caseId) : null;
      const metaTrackingId = metadata.trackingId != null ? String(metadata.trackingId) : null;
      const metaAppRef = metadata.applicationReference != null ? String(metadata.applicationReference) : null;

      if (notificationScope.type === 'case') {
        // Only notifications tied to this case.
        return metaCaseId === id;
      }

      // Application workspace: route parameter is typically iset_case.id (numeric),
      // but we also support tracking-id routes if they exist.
      if (isNumericId) {
        return metaCaseId === id;
      }
      return metaTrackingId === id || metaAppRef === id;
    });
    return sortNotificationsForDisplay(filteredNotifications, notificationSortMode);
  }, [notifications, notificationScope, notificationSortMode]);

  const loadNotifications = useCallback(async ({ scrollIntoView = false } = {}) => {
    const list = await fetchNotifications();
    if (scrollIntoView && list?.length) {
      window.requestAnimationFrame(() => flashbarRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }));
    }
    return list;
  }, [fetchNotifications]);

  const loadServiceAnnouncement = useCallback(async () => {
    try {
      const response = await apiFetch('/api/service-announcement/current');
      let data = null;
      try { data = await response.json(); } catch { data = null; }
      if (!response.ok) {
        throw new Error(data?.error || 'Failed to load service announcement');
      }
      setServiceAnnouncement(data && typeof data === 'object' ? data : null);
      return data;
    } catch (error) {
      console.error('[service-announcement] load failed', error);
      setServiceAnnouncement(null);
      return null;
    }
  }, []);

  useEffect(() => {
    loadServiceAnnouncement();
    const intervalId = window.setInterval(() => {
      loadServiceAnnouncement();
    }, SERVICE_ANNOUNCEMENT_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [loadServiceAnnouncement]);

  useEffect(() => {
    if (!isMaintenanceAnnouncementVisible(serviceAnnouncement)) {
      setAnnouncementNow(new Date());
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      setAnnouncementNow(new Date());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [serviceAnnouncement]);

  const handleDismissNotification = useCallback(async (notificationId) => {
    try {
      const response = await apiFetch(`/api/me/notifications/${notificationId}/dismiss`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || `Failed to dismiss notification (${response.status})`);
      }
      removeNotification(notificationId);
      return true;
    } catch (error) {
      console.error('[notifications] dismiss failed', error);
      return false;
    }
  }, [removeNotification]);

  const handleDismissAllNotifications = useCallback(async () => {
    if (dismissAllInFlight.current) return;
    const ids = scopedNotifications.filter(item => item && item.dismissible !== false && item.dismissible !== 0).map(item => item.id);
    dismissAllInFlight.current = true;
    setDismissingNotifications(true);
    setNotificationDismissError('');
    try {
      let failed = false;
      // Snapshot the visible stack: newly arriving notifications are not dismissed.
      // Reuse the existing per-user endpoint, without changing delivery or history.
      for (const id of ids) {
        if (!await handleDismissNotification(id)) failed = true;
      }
      if (failed) setNotificationDismissError('Some notifications could not be dismissed. Try again.');
    } finally {
      dismissAllInFlight.current = false;
      setDismissingNotifications(false);
    }
  }, [scopedNotifications, handleDismissNotification]);

  const handleNotificationSortModeChange = useCallback(({ detail }) => {
    const nextSortMode = normalizeNotificationSortMode(detail?.selectedId);
    setNotificationSortMode(nextSortMode);
    persistNotificationSortMode(nextSortMode);
  }, []);

  const mapSeverityToType = useCallback((severity = 'info') => {
    const normalised = String(severity).toLowerCase();
    if (normalised === 'critical' || normalised === 'error') return 'error';
    if (normalised === 'warning' || normalised === 'warn') return 'warning';
    if (normalised === 'success') return 'success';
    return 'info';
  }, []);

  const notificationFlashbarItems = useMemo(() =>
    scopedNotifications
      .filter(n => n)
      .map(n => {
        const metadata = parseNotificationMetadata(n.metadata);
        const eventKey = n.event_key || metadata.event_key || metadata.eventKey || null;
        const typeFromSeverity = mapSeverityToType(n.severity);
        const flashType = eventKey === 'reminder_overdue' ? 'error' : typeFromSeverity;
        const timestampLabel = formatNotificationTimestamp(
          n.delivered_at || n.created_at,
          notificationViewerTimeZone
        );
        const daysOverdue = (() => {
          if (Number.isFinite(metadata.overdue_days) && metadata.overdue_days > 0) return Math.floor(metadata.overdue_days);
          if (Number.isFinite(metadata.overdueDays) && metadata.overdueDays > 0) return Math.floor(metadata.overdueDays);
          const days = getReminderBusinessDayDiffDays(metadata.due_at || metadata.dueAt, new Date());
          return days && days > 0 ? days : null;
        })();
        const caseId = metadata.caseId || null;
        const trackingId = metadata.trackingId || null;
        const caseNumber = metadata.caseNumber || null;
        const appReference = metadata.applicationReference || null;
        const applicationId = metadata.applicationId || metadata.application_id || null;
        const isCaseManaged = metadata.isCaseManaged === true;
        const appealId = metadata.appealId || metadata.appeal_id || null;
        const href = buildNotificationTargetPath({
          appealId,
          caseId,
          applicationId,
          trackingId,
          isCaseManaged,
        });
        const linkColor = (flashType === 'info' || flashType === 'success') ? 'inverted' : 'normal';
        const openApplicationTarget = caseNumber || appReference || trackingId || null;
        const linkLabel = appealId ? 'Review appeal' : eventKey === 'document_signed'
          ? (openApplicationTarget ? `Open application ${openApplicationTarget}` : 'Open application')
          : applicationId
            ? (appReference ? `View application ${appReference}` : 'View application')
            : caseId
            ? (caseNumber ? `View case ${caseNumber}` : 'View case')
            : appReference
              ? `View application ${appReference}`
              : 'View application';
        const overdueSuffix = eventKey === 'reminder_overdue' && daysOverdue
          ? ` • ${daysOverdue} day${daysOverdue === 1 ? '' : 's'} overdue`
          : '';
        const content = href ? (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'baseline' }}>
            <span>{n.message}{overdueSuffix}</span>
            <Link href={href} color={linkColor}>{linkLabel}</Link>
          </div>
        ) : `${n.message || ''}${overdueSuffix}`;
        const header = n.title
          ? (
            timestampLabel
              ? (
                <span>
                  {n.title}
                  <span style={{ fontWeight: 'normal' }}>{` (${timestampLabel})`}</span>
                </span>
              )
              : n.title
          )
          : undefined;
        return {
          type: flashType,
          header,
          content,
          dismissible: n.dismissible !== false && n.dismissible !== 0,
          onDismiss: () => handleDismissNotification(n.id),
          id: `notification-${n.id}`,
        };
      }),
  [scopedNotifications, handleDismissNotification, mapSeverityToType, notificationViewerTimeZone]);

  const serviceAnnouncementFlashbarItem = useMemo(() => {
    const display = buildMaintenanceAnnouncementDisplay(serviceAnnouncement, { now: announcementNow, locale: 'en' });
    if (!display) return null;
    const content = display.detail
      ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span>{display.message}</span>
          <span>{display.detail}</span>
        </div>
      )
      : display.message;
    return {
      id: 'service-announcement',
      type: serviceAnnouncement?.severity === 'error' ? 'error' : 'warning',
      header: display.title,
      content,
      dismissible: false,
    };
  }, [serviceAnnouncement, announcementNow]);

  const notificationSortControl = useMemo(() => {
    if (notificationFlashbarItems.length < 2 && !dismissingNotifications && !notificationDismissError) return null;
    return (
      <div className="path-notifications-sort-control">
        {notificationDismissError && <span role="alert">{notificationDismissError}</span>}
        {notificationFlashbarItems.length > 1 && <SegmentedControl
          label="Notification sort order"
          selectedId={notificationSortMode}
          onChange={handleNotificationSortModeChange}
          options={[
            { id: NOTIFICATION_SORT_MODES.CHRONOLOGICAL, text: 'Newest' },
            { id: NOTIFICATION_SORT_MODES.URGENCY, text: 'Urgency' },
          ]}
        />}
        <Button variant="link" loading={dismissingNotifications} disabled={!notificationFlashbarItems.length}
          onClick={handleDismissAllNotifications}>Dismiss all</Button>
      </div>
    );
  }, [handleNotificationSortModeChange, notificationFlashbarItems.length, notificationSortMode, dismissingNotifications, notificationDismissError, handleDismissAllNotifications]);

  const refreshNotifications = useCallback(() => loadNotifications({ scrollIntoView: true }), [loadNotifications]);

  const [aiContext, setAiContext] = useState(AdminDashboardHelp.aiContext || ""); // State to hold AI context

  const toggleHelpPanel = useCallback((content, title = "Help and Tutorials", context = "") => {
    setCurrentHelpContent(content);
    setHelpPanelTitle(title);
    const fallbackContext = AdminDashboardHelp.aiContext || "";
    const nextContext = typeof context === 'string' && context.trim() ? context : fallbackContext;
    setAiContext(nextContext); // Set the AI context
    setIsHelpPanelOpen(true);
  }, []);

  const markTutorialCompleted = useCallback((tutorial) => {
    const tutorialId = getTutorialId(tutorial);
    if (!tutorialId) return;
    persistTutorialStatus(tutorialId, 'completed');
  }, [persistTutorialStatus]);

  const endTutorial = useCallback((tutorial) => {
    const tutorialId = getTutorialId(tutorial);
    if (tutorialId) {
      persistTutorialStatus(tutorialId, 'dismissed');
    }
    setCurrentTutorial(null);
  }, [persistTutorialStatus]);

	  const handleIntroNotNow = useCallback(() => {
	    setIntroPromptVisible(false);
	    if (homeIntroTutorialId) {
	      persistTutorialStatus(homeIntroTutorialId, 'dismissed');
	    }
	  }, [homeIntroTutorialId, persistTutorialStatus]);

	  const handleIntroStartTour = useCallback(() => {
	    setIntroPromptVisible(false);
	    const introTutorial = (tutorials || []).find(tutorial => getTutorialId(tutorial) === homeIntroTutorialId);
      const runnableTutorial = cloneTutorialForRun(introTutorial);
	    toggleHelpPanel(
	      <IsetCoordinatorIntroTourHelp
	        tutorial={runnableTutorial}
	        onRestartTutorial={() => {
	          if (!runnableTutorial) return;
	          setCurrentTutorial(null);
	          window.setTimeout(() => setCurrentTutorial(cloneTutorialForRun(runnableTutorial)), 0);
	        }}
	        onEndTutorial={() => endTutorial(runnableTutorial)}
	      />,
	      'PATH quick start'
	    );
	    if (runnableTutorial) {
	      setCurrentTutorial(runnableTutorial);
	    }
	  }, [endTutorial, homeIntroTutorialId, toggleHelpPanel, tutorials]);

	  const handleRestartTutorial = useCallback((tutorial) => {
	    const tutorialId = getTutorialId(tutorial);
	    if (!tutorialId) return;
	    setCurrentTutorial(null);
	    window.setTimeout(() => {
	      window.dispatchEvent(
	        new CustomEvent('tutorials:start', {
	          detail: { tutorialId }
	        })
	      );
	    }, 0);
	  }, []);

		  const resolveTutorialStartPath = useCallback(async (tutorial) => {
		    const category = tutorial?.category || null;
        const currentPath = location?.pathname || "/";

	    if (isHomeIntroTutorial(tutorial)) {
	      return '/';
	    }

	    // Tutorials that depend on hotspots inside the application workspace need a case context.
	    if (category === 'application-workspace' || category === 'nwac-assessment' || category === 'case-workspace') {
          if (category === 'application-workspace' && /^\/application-case\/\d+/.test(currentPath)) {
            return currentPath;
          }
          if (category === 'nwac-assessment' && /^\/application-case\/\d+/.test(currentPath)) {
            return currentPath;
          }
          if (category === 'case-workspace' && /^\/cases\/\d+/.test(currentPath)) {
            return currentPath;
          }

	      const fetchFirstCaseIdFromCases = async (statusList) => {
	        const params = new URLSearchParams({
	          page: '1',
	          pageSize: '1',
	          sort: 'updatedAt',
	          direction: 'desc',
	        });
	        if (statusList) params.set('status', statusList);
	        const resp = await apiFetch(`/api/cases?${params.toString()}`);
	        if (!resp.ok) return null;
	        const json = await resp.json().catch(() => null);
	        const items = Array.isArray(json?.items) ? json.items : [];
	        if (!items.length) return null;
	        const row = items[0] || {};
	        const caseId = row.id || row.caseId || row.case_id || null;
	        const n = Number(caseId);
	        return Number.isFinite(n) && n > 0 ? n : null;
	      };

	      const fetchFirstCaseIdFromApplications = async statusList => {
	        const params = new URLSearchParams({
	          limit: '1',
	          offset: '0',
	          sort: 'updatedAt',
	          direction: 'desc',
	        });
	        if (statusList) params.set('status', statusList);
	        const resp = await apiFetch(`/api/applications?${params.toString()}`);
	        if (!resp.ok) return null;
	        const json = await resp.json().catch(() => null);
	        const items = Array.isArray(json?.rows) ? json.rows : [];
	        if (!items.length) return null;
	        const row = items[0] || {};
	        const caseId = row.case_id || row.caseId || null;
	        const n = Number(caseId);
	        return Number.isFinite(n) && n > 0 ? n : null;
	      };
	
	      if (category === 'case-workspace') {
	        const anyCaseId = await fetchFirstCaseIdFromCases(null);
	        if (anyCaseId) return `/cases/${anyCaseId}`;
	        return '/iset/cases';
	      }

	      // Prefer an actual pending-decision application for NWAC tutorials when possible.
	      if (category === 'nwac-assessment') {
	        const pendingApplicationCaseId = await fetchFirstCaseIdFromApplications('pending_approval');
	        if (pendingApplicationCaseId) return `/application-case/${pendingApplicationCaseId}`;
	        const pending = await fetchFirstCaseIdFromCases('pending_approval');
	        if (pending) return `/application-case/${pending}`;
	      }
	
	      const any = await fetchFirstCaseIdFromCases(null);
	      if (any) return `/application-case/${any}`;
	
	      // Fallback: at least bring them to the place where they can open an application.
	      return '/case-assignment-dashboard';
	    }
	
	    return null;
	  }, [location?.pathname]);
	
	  const openHelpForTutorial = useCallback((tutorial) => {
	    const category = tutorial?.category || null;
	
	    if (isHomeIntroTutorial(tutorial)) {
	      toggleHelpPanel(
	        <IsetCoordinatorIntroTourHelp
	          tutorial={tutorial}
	          onRestartTutorial={() => handleRestartTutorial(tutorial)}
	          onEndTutorial={() => endTutorial(tutorial)}
	        />,
	        'PATH quick start'
	      );
	      return;
	    }
	    if (category === 'application-workspace') {
	      toggleHelpPanel(
	        <ApplicationCaseDashboardHelp
	          tutorial={tutorial}
	          onRestartTutorial={() => handleRestartTutorial(tutorial)}
	          onEndTutorial={() => endTutorial(tutorial)}
	        />,
	        'Application workspace quick start',
	        ApplicationCaseDashboardHelp.aiContext || ''
	      );
	      return;
	    }
	    if (category === 'case-workspace') {
	      toggleHelpPanel(
	        <CaseWorkspaceHelp
	          tutorial={tutorial}
	          onRestartTutorial={() => handleRestartTutorial(tutorial)}
	          onEndTutorial={() => endTutorial(tutorial)}
	        />,
	        'Case workspace quick start',
	        CaseWorkspaceHelp.aiContext || ''
	      );
	      return;
	    }
	    if (category === 'nwac-assessment') {
	      toggleHelpPanel(
	        <NwacAssessmentHelp
	          tutorial={tutorial}
	          onRestartTutorial={() => handleRestartTutorial(tutorial)}
	          onEndTutorial={() => endTutorial(tutorial)}
	        />,
	        'NWAC decision quick start',
	        NwacAssessmentHelp.aiContext || ''
	      );
	      return;
	    }
	
	    // Default: open the general help panel, but keep the tutorial running.
	    toggleHelpPanel(<ApplicationAssessmentHelp />, 'Help and Tutorials', ApplicationAssessmentHelp.aiContext || '');
	  }, [endTutorial, handleRestartTutorial, toggleHelpPanel]);

	  const requestApplicationWorkspaceLayoutReset = useCallback(() => {
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage?.setItem(TUTORIAL_APP_LAYOUT_RESET_FLAG, '1');
      }
    } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent('applicationAssessment:resetLayout'));
    } catch (_) {}
	  }, []);

  const requestCaseWorkspaceLayoutReset = useCallback(() => {
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage?.setItem(TUTORIAL_CASE_LAYOUT_RESET_FLAG, '1');
      }
    } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent('iset-case-workspace:resetLayout'));
    } catch (_) {}
  }, []);

	  const activePagePromptTutorial = useMemo(() => {
	    const id = pageTutorialPrompt?.tutorialId;
	    if (!id) return null;
	    return (tutorials || []).find(t => getTutorialId(t) === id) || null;
	  }, [pageTutorialPrompt?.tutorialId, tutorials]);

	  const handlePageTutorialSkip = useCallback(() => {
	    const id = pageTutorialPrompt?.tutorialId;
	    setPageTutorialPrompt({ visible: false, tutorialId: null });
	    if (id) {
	      persistTutorialStatus(id, 'dismissed');
	    }
	  }, [pageTutorialPrompt?.tutorialId, persistTutorialStatus]);

	  const handlePageTutorialStart = useCallback(() => {
	    const tutorial = activePagePromptTutorial;
	    setPageTutorialPrompt({ visible: false, tutorialId: null });
	    if (!tutorial) return;
      const runnableTutorial = cloneTutorialForRun(tutorial);
      if (!runnableTutorial) return;
      const category = tutorial?.category || null;
      const requiresApplicationWorkspaceLayout =
        category === 'application-workspace' || category === 'nwac-assessment';
      if (requiresApplicationWorkspaceLayout) {
        requestApplicationWorkspaceLayoutReset();
      }
      if (category === 'case-workspace') {
        requestCaseWorkspaceLayoutReset();
      }
      (async () => {
        try {
          openHelpForTutorial(runnableTutorial);
          const target = await resolveTutorialStartPath(runnableTutorial);
          if (target && (location?.pathname || '/') !== target) {
            try { history.push(target); } catch (_) {}
          }
        } finally {
          setCurrentTutorial(runnableTutorial);
        }
      })();
	  }, [
      activePagePromptTutorial,
      history,
      location?.pathname,
      openHelpForTutorial,
      requestApplicationWorkspaceLayoutReset,
      requestCaseWorkspaceLayoutReset,
      resolveTutorialStartPath
    ]);
	
	  const handleStartTutorial = useCallback(({ detail }) => {
	    const tutorial = detail?.tutorial || null;
	    if (!tutorial) {
	      setCurrentTutorial(null);
	      return;
	    }
      const runnableTutorial = cloneTutorialForRun(tutorial);
      if (!runnableTutorial) {
        setCurrentTutorial(null);
        return;
      }
      const category = tutorial?.category || null;
      const requiresApplicationWorkspaceLayout =
        category === 'application-workspace' || category === 'nwac-assessment';
      if (requiresApplicationWorkspaceLayout) {
        requestApplicationWorkspaceLayoutReset();
      }
      if (category === 'case-workspace') {
        requestCaseWorkspaceLayoutReset();
      }
	
	    (async () => {
	      try {
	        openHelpForTutorial(runnableTutorial);
	        const target = await resolveTutorialStartPath(runnableTutorial);
	        if (target && (location?.pathname || '/') !== target) {
	          try { history.push(target); } catch (_) {}
	        }
	      } finally {
	        setCurrentTutorial(runnableTutorial);
	      }
	    })();
	  }, [history, location?.pathname, openHelpForTutorial, requestApplicationWorkspaceLayoutReset, requestCaseWorkspaceLayoutReset, resolveTutorialStartPath]);

  useEffect(() => {
    const handler = (event) => {
      const tutorialId = event?.detail?.tutorialId;
      if (!tutorialId) return;
      const tutorial = (tutorials || []).find(t => getTutorialId(t) === tutorialId);
      if (!tutorial) return;
      handleStartTutorial({ detail: { tutorial } });
    };
    window.addEventListener('tutorials:start', handler);
    return () => window.removeEventListener('tutorials:start', handler);
  }, [handleStartTutorial, tutorials]);

  useEffect(() => {
    const handler = () => {
      const active = currentTutorialRef.current;
      if (active) {
        endTutorial(active);
      }
    };
    window.addEventListener('tutorials:end', handler);
    return () => window.removeEventListener('tutorials:end', handler);
  }, [endTutorial]);

	  const handleExitTutorial = useCallback(() => {
	    endTutorial(currentTutorial);
	  }, [currentTutorial, endTutorial]);

  const handleFinishTutorial = useCallback(() => {
    if (currentTutorial) {
      markTutorialCompleted(currentTutorial);
    }
    setCurrentTutorial(null);
  }, [currentTutorial, markTutorialCompleted]);

  useEffect(() => {
    currentTutorialRef.current = currentTutorial;
  }, [currentTutorial]);

  useEffect(() => {
    const handleTutorialDismissClick = (event) => {
      const target = event?.target;
      if (!(target instanceof Element)) return;
      const dismissButton = target.closest('button[aria-label="Close tutorial"]');
      if (!dismissButton) return;
      const activeTutorial = currentTutorialRef.current;
      if (!activeTutorial) return;
      window.setTimeout(() => {
        const latestTutorial = currentTutorialRef.current;
        if (latestTutorial) {
          endTutorial(latestTutorial);
        }
      }, 0);
    };
    document.addEventListener('click', handleTutorialDismissClick, true);
    return () => document.removeEventListener('click', handleTutorialDismissClick, true);
  }, [endTutorial]);

  const updateBreadcrumbs = useCallback((newBreadcrumbs) => {
    setBreadcrumbs((currentBreadcrumbs) => {
      const breadcrumbsChanged =
        JSON.stringify(newBreadcrumbs) !== JSON.stringify(currentBreadcrumbs);
      return breadcrumbsChanged ? newBreadcrumbs : currentBreadcrumbs;
    });
  }, []);

  const handleSplitPanelPreferencesChange = (newPreferences) => {
    setSplitPanelPreferences(newPreferences);
  };

  const handleItemSelect = (item) => {
    setAvailableItems((prevItems) => prevItems.filter((availableItem) => availableItem.id !== item.id));
  };

  const [chatVisible, setChatVisible] = useState(false);

  const captureFeedbackContextSnapshot = useCallback(() => {
    const breadcrumbItems = Array.isArray(breadcrumbs)
      ? breadcrumbs.map(item => ({
          text: typeof item?.text === 'string' ? item.text : '',
          href: typeof item?.href === 'string' ? item.href : '',
        }))
      : [];
    const pathValue = `${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`;
    let urlValue = pathValue;
    let viewport = null;
    let browser = null;
    if (typeof window !== 'undefined') {
      urlValue = window.location?.href || pathValue;
      viewport = {
        width: window.innerWidth || null,
        height: window.innerHeight || null,
      };
    }
    if (typeof navigator !== 'undefined') {
      browser = {
        userAgent: navigator.userAgent || null,
        language: navigator.language || null,
        languages: Array.isArray(navigator.languages) ? navigator.languages : [],
      };
    }
    let timeZone = null;
    try {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch (_) {
      timeZone = null;
    }
    return {
      capturedAt: new Date().toISOString(),
      pageTitle:
        (typeof document !== 'undefined' && document.title ? document.title : null) ||
        breadcrumbItems[breadcrumbItems.length - 1]?.text ||
        'PATH',
      path: pathValue || '/',
      url: urlValue || pathValue || '/',
      breadcrumbs: breadcrumbItems,
      viewport,
      browser,
      timeZone,
    };
  }, [breadcrumbs, location?.hash, location?.pathname, location?.search]);

  const openFeedbackComposer = useCallback((requestedType = 'bug') => {
    setFeedbackRequestedType(requestedType === 'change_request' ? 'change_request' : 'bug');
    setFeedbackRequestedContextSnapshot(captureFeedbackContextSnapshot());
    setFeedbackOpenRequestId(currentValue => currentValue + 1);
    setFeedbackVisible(true);
  }, [captureFeedbackContextSnapshot]);

  const openFeedbackReview = useCallback((reportId) => {
    const numericId = Number(reportId);
    if (!Number.isFinite(numericId) || numericId <= 0) return;
    setFeedbackReviewReportId(numericId);
    setFeedbackReviewOpenRequestId(currentValue => currentValue + 1);
    setFeedbackReviewVisible(true);
  }, []);

  const handleFeedbackSubmitSuccess = useCallback((report) => {
    setFeedbackVisible(false);
    setFeedbackSubmissionConfirmation(report || {});
  }, []);

  useEffect(() => {
    setIsHelpPanelOpen(false);
    setChatVisible(false);
    if (location?.pathname === '/finance/budgets') {
      setCurrentHelpContent(<FinanceBudgetsHelp />);
      setHelpPanelTitle('Budgets');
      setAiContext(FinanceBudgetsHelp.aiContext);
    } else if (location?.pathname === '/finance/overview') {
      setCurrentHelpContent(<FinanceOverviewHelp />);
      setHelpPanelTitle('Finance Overview');
      setAiContext(FinanceOverviewHelp.aiContext);
    } else if (location?.pathname === '/reminders') {
      setCurrentHelpContent(<RemindersHelp />);
      setHelpPanelTitle('Reminders');
      setAiContext(RemindersHelp.aiContext);
    }
  }, [location?.pathname, location?.search]);

  const openPaletteInTools = useCallback((items) => {
    try { setAvailableItems(items || []); } catch {}
    setSplitPanelOpen(true);
  }, [setSplitPanelOpen]);

  useEffect(() => {
    const handleTopNavHelp = (event) => {
      const detail = event?.detail || {};
      const content = detail.content || <AdminConsoleIntroHelp />;
      const title = detail.title || 'Admin Console Help';
      const context = detail.context || AdminConsoleIntroHelp.aiContext || AdminDashboardHelp.aiContext || '';
      toggleHelpPanel(content, title, context);
    };
    window.addEventListener('help:open-topnav', handleTopNavHelp);
    return () => window.removeEventListener('help:open-topnav', handleTopNavHelp);
  }, [toggleHelpPanel]);

  useEffect(() => {
    const handleOpenFeedbackComposer = event => {
      const requestedType = event?.detail?.reportType;
      openFeedbackComposer(requestedType);
    };
    window.addEventListener('admin-feedback:open-composer', handleOpenFeedbackComposer);
    return () => window.removeEventListener('admin-feedback:open-composer', handleOpenFeedbackComposer);
  }, [openFeedbackComposer]);

  useEffect(() => {
    const handleOpenFeedbackReview = event => {
      openFeedbackReview(event?.detail?.reportId);
    };
    window.addEventListener('admin-feedback:open-review', handleOpenFeedbackReview);
    return () => window.removeEventListener('admin-feedback:open-review', handleOpenFeedbackReview);
  }, [openFeedbackReview]);

  // Listen for page requests to open the tools palette (avoids prop drilling)
  useEffect(() => {
    const onOpenPalette = (e) => {
      try {
        const items = (e && e.detail && e.detail.items) || [];
        setAvailableItems(items);
        setSplitPanelOpen(true);
      } catch {}
    };
    window.addEventListener('tools:open-palette', onOpenPalette);
    return () => window.removeEventListener('tools:open-palette', onOpenPalette);
  }, [setSplitPanelOpen]);

  return (
    <LocationProvider>
      <MessagingProvider>
        <FloatingAppealWindow />
        <InteractionPanel />
        <FloatingMessageWindow chatVisible={chatVisible} />
        <FloatingChat
          visible={chatVisible}
          aiContext={aiContext}
          pathname={location.pathname}
          onClose={() => setChatVisible(false)}
          role={effectiveRole}
          title={helpPanelTitle}
        />
        <FloatingFeedbackReporter
          visible={feedbackVisible}
          chatVisible={chatVisible}
          reviewVisible={feedbackReviewVisible}
          openRequestId={feedbackOpenRequestId}
          requestedReportType={feedbackRequestedType}
          requestedContextSnapshot={feedbackRequestedContextSnapshot}
          onSubmitSuccess={handleFeedbackSubmitSuccess}
          onClose={() => setFeedbackVisible(false)}
        />
        <FloatingFeedbackReviewPanel
          visible={feedbackReviewVisible}
          chatVisible={chatVisible}
          reporterVisible={feedbackVisible}
          reportId={feedbackReviewReportId}
          openRequestId={feedbackReviewOpenRequestId}
          onClose={() => setFeedbackReviewVisible(false)}
        />
        <Modal
          visible={Boolean(feedbackSubmissionConfirmation)}
          header={`${getReportTypeLabel(feedbackSubmissionConfirmation?.reportType || 'bug')} submitted`}
          closeAriaLabel="Close submission confirmation"
          onDismiss={() => setFeedbackSubmissionConfirmation(null)}
          footer={(
            <SpaceBetween size="xs" direction="horizontal">
              <Button onClick={() => setFeedbackSubmissionConfirmation(null)}>
                Close
              </Button>
              {normalizedEffectiveRole === 'system administrator' && Number(feedbackSubmissionConfirmation?.id) > 0 ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    const reportId = Number(feedbackSubmissionConfirmation?.id);
                    setFeedbackSubmissionConfirmation(null);
                    openFeedbackReview(reportId);
                  }}
                >
                  Open report
                </Button>
              ) : null}
            </SpaceBetween>
          )}
        >
          <SpaceBetween size="m">
            <Box variant="p">
              {`${getReportTypeLabel(feedbackSubmissionConfirmation?.reportType || 'bug')} #${feedbackSubmissionConfirmation?.id || 'new'} was submitted successfully.`}
            </Box>
            <Container header={<Header variant="h3">Submission summary</Header>}>
              <SpaceBetween size="s">
                <Box>
                  <Box variant="awsui-key-label">Severity</Box>
                  <Box>{getSeverityLabel(feedbackSubmissionConfirmation?.severity)}</Box>
                </Box>
                <Box>
                  <Box variant="awsui-key-label">Attachments</Box>
                  <Box>{Number(feedbackSubmissionConfirmation?.attachmentCount || 0)}</Box>
                </Box>
              </SpaceBetween>
            </Container>
          </SpaceBetween>
        </Modal>
        <TutorialsContext.Provider value={{ tutorials }}>
          <AnnotationContext
            currentTutorial={currentTutorial}
            onStartTutorial={handleStartTutorial}
            onExitTutorial={handleExitTutorial}
            onFinish={handleFinishTutorial}
            i18nStrings={annotationContextI18nStrings}
          >
	            {introPromptVisible && (
	              <Modal
	                visible={introPromptVisible}
	                header="PATH quick start"
	                closeAriaLabel="Close quick start prompt"
	                onDismiss={handleIntroNotNow}
                footer={
                  <SpaceBetween size="xs" direction="horizontal">
                    <Button onClick={handleIntroNotNow}>Not now</Button>
                    <Button variant="primary" onClick={handleIntroStartTour}>Start tour</Button>
                  </SpaceBetween>
                }
              >
                <SpaceBetween size="m">
                  <Box>
                    Welcome to PATH. This short intro is for staff who are still getting used to the system. It will show you how to decide what to work on first, open the right file, and find help when you are unsure.
                  </Box>
                  <Box>
                    You can replay tutorials later from dashboard and widget Info links, or reset tutorial progress from the Tutorials page under Support.
                  </Box>
                </SpaceBetween>
	              </Modal>
	            )}
	            {pageTutorialPrompt.visible && activePagePromptTutorial && (
	              <Modal
	                visible={pageTutorialPrompt.visible}
	                header={activePagePromptTutorial.title || 'PATH quick start'}
	                closeAriaLabel="Close tutorial prompt"
	                onDismiss={handlePageTutorialSkip}
	                footer={
	                  <SpaceBetween size="xs" direction="horizontal">
	                    <Button onClick={handlePageTutorialSkip}>Skip</Button>
	                    <Button variant="primary" onClick={handlePageTutorialStart}>Start tutorial</Button>
	                  </SpaceBetween>
	                }
	              >
	                <SpaceBetween size="m">
	                  <Box variant="p">
	                    This workspace tour is meant to help you understand what PATH expects you to do on this page, where the main work happens, and what should be recorded before you move on.
	                  </Box>
	                  <Box variant="p">
	                    You can replay tutorials from dashboard and widget Info links in the help panel, and reset tutorial progress from the Tutorials page under Support.
	                  </Box>
	                </SpaceBetween>
	              </Modal>
	            )}
	            <AppLayout
	              navigationOpen={isNavigationOpen}
	              onNavigationChange={({ detail }) => setIsNavigationOpen(detail.open)}
	              navigation={
	                <SideNavigation
                  showTutorialHotspots={isHomeIntroTutorial(currentTutorial)}
                  notificationCount={scopedNotifications.length}
                  refreshNotifications={refreshNotifications}
                  notificationsLoading={notificationsLoading}
                />
              }
              notifications={
                <div ref={flashbarRef} className="path-notifications-region">
                  {notificationSortControl}
                  <Flashbar
                    stackItems
                    items={serviceAnnouncementFlashbarItem ? [serviceAnnouncementFlashbarItem, ...notificationFlashbarItems] : notificationFlashbarItems}
                  />
                </div>
              }
              toolsOpen={isHelpPanelOpen}
              onToolsChange={({ detail }) => setIsHelpPanelOpen(detail.open)}
              tools={
                <HelpPanel
                  header={
                    <Header
                      variant="h2"
                      actions={
                        <Button
                          onClick={() => setChatVisible(!chatVisible)}
                          variant="primary"
                        >
                          {chatVisible ? "Close AI" : "Ask the AI"}
                        </Button>
                      }
                    >
                      {helpPanelTitle}
                    </Header>
                  }
                >
                  {currentHelpContent}
                </HelpPanel>
              }
              splitPanelOpen={splitPanelOpen}
              onSplitPanelToggle={({ detail }) => setSplitPanelOpen(detail.open)}
              splitPanel={
                <CustomSplitPanel
                  availableItems={availableItems}
                  handleItemSelect={handleItemSelect}
                  splitPanelSize={splitPanelSize}
                  setSplitPanelSize={setSplitPanelSize}
                  splitPanelOpen={splitPanelOpen}
                  setSplitPanelOpen={setSplitPanelOpen}
                />
              }
              splitPanelPreferences={splitPanelPreferences}
              onSplitPanelPreferencesChange={handleSplitPanelPreferencesChange}
	              content={
	                <SpaceBetween size="l">
	                  <AppRoutes
	                    toggleHelpPanel={toggleHelpPanel}
	                    updateBreadcrumbs={updateBreadcrumbs}
	                    setSplitPanelOpen={setSplitPanelOpen}
                    splitPanelOpen={splitPanelOpen}
                    setSplitPanelSize={setSplitPanelSize}
                    splitPanelSize={splitPanelSize}
                    setAvailableItems={setAvailableItems}
                    openPaletteInTools={openPaletteInTools}
                    breadcrumbs={breadcrumbs}
                    helpMessages={helpMessages}
                    aiContext={AdminDashboardHelp.aiContext} // Use the static aiContext property
                  />

                </SpaceBetween>
              }
            />
          </AnnotationContext>
        </TutorialsContext.Provider>
      </MessagingProvider>
    </LocationProvider>
  );
};

export default AppContent;




